/**
 * Pre-login banner dismissals.
 *
 * Signed-in banners record dismissals as per-user `BannerDismissal` rows. The
 * sign-in page has no user, so dismissal is per-browser and lives in
 * localStorage instead. The parse/filter/serialize helpers are pure so they
 * unit-test without a DOM; only `readDismissedBannerIds` /
 * `rememberDismissedBannerId` touch storage.
 */

export const PRE_LOGIN_BANNER_DISMISSALS_KEY = 'ordo.preLoginBannerDismissals'

/**
 * Tolerant parse: anything that isn't a JSON array of strings (hand-edited
 * value, a legacy format, junk from another tab) reads as "nothing dismissed"
 * rather than throwing on a page that must always render.
 */
export function parseDismissedBannerIds(raw: string | null): string[] {
  if (!raw) {
    return []
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
  } catch {
    return []
  }
}

export function serializeDismissedBannerIds(ids: string[]): string {
  return JSON.stringify([...new Set(ids)])
}

/**
 * Drops banners this browser already dismissed. A non-dismissible banner is
 * never hidden — the admin marked it as must-read, and a stale stored id (or
 * a banner later switched to non-dismissible) must not suppress it.
 */
export function undismissedBanners<T extends { id: string; dismissible: boolean }>(
  banners: T[],
  dismissedIds: string[],
): T[] {
  const dismissed = new Set(dismissedIds)
  return banners.filter((banner) => !banner.dismissible || !dismissed.has(banner.id))
}

/** Storage can throw (Safari private mode, disabled cookies) — never fatal here. */
export function readDismissedBannerIds(): string[] {
  try {
    return parseDismissedBannerIds(window.localStorage.getItem(PRE_LOGIN_BANNER_DISMISSALS_KEY))
  } catch {
    return []
  }
}

export function rememberDismissedBannerId(bannerId: string): string[] {
  const next = [...readDismissedBannerIds(), bannerId]
  try {
    window.localStorage.setItem(PRE_LOGIN_BANNER_DISMISSALS_KEY, serializeDismissedBannerIds(next))
  } catch {
    // Dismissal doesn't survive the reload; the banner still closes for now.
  }
  return next
}
